import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false); // user menu
  const [aboutDropdown, setAboutDropdown] = useState(false); // about submenu
  const [menuOpen, setMenuOpen] = useState(false); // main drawer
  const [isMobile, setIsMobile] = useState(false);

  const aboutCloseTimer = useRef(null);
  const userCloseTimer = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    const status = localStorage.getItem("isLoggedIn") === "true";
    const currentUser = localStorage.getItem("currentUser");

    setIsLoggedIn(status);
    setIsAdmin(currentUser && currentUser.trim().toLowerCase() === "admin");

    // close menus on route change
    setMenuOpen(false);
    setDropdownOpen(false);
    setAboutDropdown(false);

    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    if (userCloseTimer.current) clearTimeout(userCloseTimer.current);
  }, [location]);

  const closeAllMenus = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setAboutDropdown(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("currentUser");
    alert("Successfully logged out!");
    closeAllMenus();
    navigate("/");
  };

  // About hover (desktop only)
  const openAboutHover = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    setAboutDropdown(true);
  };

  const closeAboutHover = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    aboutCloseTimer.current = setTimeout(() => setAboutDropdown(false), 350);
  };

  // User hover (desktop only)
  const openUserHover = () => {
    if (userCloseTimer.current) clearTimeout(userCloseTimer.current);
    setDropdownOpen(true);
  };

  const closeUserHover = () => {
    if (userCloseTimer.current) clearTimeout(userCloseTimer.current);
    userCloseTimer.current = setTimeout(() => setDropdownOpen(false), 350);
  };

  const handleUserIconClick = () => {
    if (!isLoggedIn) {
      closeAllMenus();
      navigate("/login");
      return;
    }

    setDropdownOpen((v) => !v);
    setAboutDropdown(false);
  };

  const handleHamburgerClick = () => {
    setMenuOpen((v) => !v);
    setAboutDropdown(false);
  };

  return (
    <nav id="navbar">
      {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)} />}

      {isMobile && dropdownOpen && (
        <div className="nav-overlay" onClick={() => setDropdownOpen(false)} />
      )}

      <div className="nav-container">
        <div className="nav-left">
          <h1 className="logo">
            <Link to="/" onClick={closeAllMenus}>
              HA
            </Link>
          </h1>
        </div>

        <div className="nav-right">
          <ul id="primary-navigation" className={menuOpen ? "open" : ""}>
            <li className="drawer-close-row">
              <button
                type="button"
                className="drawer-close"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </li>

            <li>
              <Link to="/" className={location.pathname === "/" ? "current" : ""} onClick={closeAllMenus}>
                Home
              </Link>
            </li>

            <li>
              <Link
                to="/booking"
                className={location.pathname === "/booking" ? "current" : ""}
                onClick={closeAllMenus}
              >
                Book Now
              </Link>
            </li>

            {/* ABOUT */}
            <li
              className="about-dropdown-wrapper"
              onMouseEnter={() => !isMobile && openAboutHover()}
              onMouseLeave={() => !isMobile && closeAboutHover()}
            >
              {!isMobile && (
                <div className="about-container">
                  <span
                    className={`dropdown-toggle ${aboutDropdown ? "hovering" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setAboutDropdown((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setAboutDropdown((v) => !v);
                    }}
                  >
                    About
                  </span>

                  {aboutDropdown && (
                    <ul className="about-dropdown" onMouseEnter={openAboutHover} onMouseLeave={closeAboutHover}>
                      <li
                        className={location.pathname === "/about" ? "active" : ""}
                        onClick={() => { navigate("/about"); closeAllMenus(); }}
                      >
                        About Our Hotel
                      </li>
                      <li
                        className={location.pathname === "/location-and-amenities" ? "active" : ""}
                        onClick={() => { navigate("/location-and-amenities"); closeAllMenus(); }}
                      >
                        Location & Amenities
                      </li>
                    </ul>
                  )}
                </div>
              )}

              {isMobile && (
                <>
                  <button
                    type="button"
                    className="mobile-about-toggle"
                    onClick={() => setAboutDropdown((v) => !v)}
                  >
                    <span>About</span>
                    <span className="mobile-about-arrow">{aboutDropdown ? "▲" : "▼"}</span>
                  </button>

                  {aboutDropdown && (
                    <ul className="mobile-about-submenu">
                      <li>
                        <Link to="/about" onClick={closeAllMenus}>About Our Hotel</Link>
                      </li>
                      <li>
                        <Link to="/location-and-amenities" onClick={closeAllMenus}>
                          Location & Amenities
                        </Link>
                      </li>
                    </ul>
                  )}
                </>
              )}
            </li>

            <li>
              <Link
                to="/contact"
                className={location.pathname === "/contact" ? "current" : ""}
                onClick={closeAllMenus}
              >
                Contact
              </Link>
            </li>
          </ul>

          <div className="nav-actions">
            <button
              className="nav-toggle"
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              aria-controls="primary-navigation"
              onClick={handleHamburgerClick}
            >
              <i className="fa-solid fa-bars"></i>
            </button>

            <div
              className="user-icon-wrapper"
              onMouseEnter={() => !isMobile && isLoggedIn && openUserHover()}
              onMouseLeave={() => !isMobile && closeUserHover()}
            >
              <button
                type="button"
                className="user-icon-circle"
                aria-label={isLoggedIn ? "Account menu" : "Go to login"}
                aria-expanded={dropdownOpen}
                onClick={handleUserIconClick}
              >
                <i className="fa-solid fa-user"></i>
              </button>

              {!isMobile && dropdownOpen && isLoggedIn && (
                <ul className="logout-dropdown">
                  {isAdmin ? (
                    <>
                      <li>
                        <Link
                          to="/users"
                          className={location.pathname === "/users" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Users
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/submissions"
                          className={location.pathname === "/submissions" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Submissions
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/bookings"
                          className={location.pathname === "/bookings" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Bookings
                        </Link>
                      </li>
                    </>
                  ) : (
                    <li>
                      <Link
                        to="/my-bookings"
                        className={location.pathname === "/my-bookings" ? "current" : ""}
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Bookings
                      </Link>
                    </li>
                  )}
                  <li onClick={handleLogout}>Logout</li>
                </ul>
              )}

              {isMobile && isLoggedIn && (
                <ul className={`logout-dropdown mobile-user-drawer ${dropdownOpen ? "open" : ""}`}>
                  <li className="drawer-close-row">
                    <button
                      type="button"
                      className="drawer-close"
                      aria-label="Close account menu"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </li>

                  {isAdmin ? (
                    <>
                      <li>
                        <Link
                          to="/users"
                          className={location.pathname === "/users" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Users
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/submissions"
                          className={location.pathname === "/submissions" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Submissions
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/bookings"
                          className={location.pathname === "/bookings" ? "current" : ""}
                          onClick={() => setDropdownOpen(false)}
                        >
                          View Bookings
                        </Link>
                      </li>
                    </>
                  ) : (
                    <li>
                      <Link
                        to="/my-bookings"
                        className={location.pathname === "/my-bookings" ? "current" : ""}
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Bookings
                      </Link>
                    </li>
                  )}

                  <li onClick={handleLogout}>Logout</li>
                </ul>
              )}

              {!isLoggedIn && location.pathname !== "/login" && (
                <Link to="/login" className="invisible-login-link" />
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;